import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Search, Volume2, Filter } from 'lucide-react';
import { fetchVoices } from '../../services/tts';
import { getLanguageDisplayName } from '../../utils/languageUtils';
import type { TTSVoice } from '../../types';
import VoiceCard from '../voice/VoiceCard';
import { setVoices } from '../../store/slices/voicesSlice';
import { RootState } from '../../store';
import LanguageSelect from '../voice/LanguageSelect';

const VoiceModels = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCount, setShowCount] = useState(9);
  const [activeFilters, setActiveFilters] = useState({
    gender: [] as string[],
    type: [] as string[],
    language: ['en-US'] as string[],
    style: [] as string[],
  });

  const voices = useSelector((state: RootState) => state.voices.voices);

  useEffect(() => {
    const loadVoices = async () => {
      try {
        setLoading(true);
        setError(null);
        const voiceList = await fetchVoices();
        dispatch(setVoices(voiceList));
      } catch (err: any) {
        setError(err.message || 'Failed to load voices');
        console.error('Error loading voices:', err);
      } finally {
        setLoading(false);
      }
    };

    loadVoices();
  }, [dispatch]);

  const voiceTypes = [
    { id: 'standard', label: 'Standard', types: ['Standard'] },
    { id: 'premium', label: 'Premium', types: ['Neural2', 'Studio', 'Wavenet'] },
    { id: 'hd', label: 'HD', types: ['HD'] }
  ];

  const styles = [
    { id: 'customerservice', label: 'Customer Service' },
    { id: 'empathetic', label: 'Empathetic' },
    { id: 'angry', label: 'Angry' },
    { id: 'sad', label: 'Sad' },
    { id: 'excited', label: 'Excited' },
    { id: 'friendly', label: 'Friendly' },
    { id: 'hopeful', label: 'Hopeful' },
    { id: 'narration-professional', label: 'Professional' },
    { id: 'newscast-casual', label: 'Newscast Casual' },
    { id: 'newscast-formal', label: 'Newscast Formal' },
    { id: 'shouting', label: 'Shouting' },
    { id: 'terrified', label: 'Terrified' },
    { id: 'unfriendly', label: 'Unfriendly' },
    { id: 'whispering', label: 'Whispering' }
  ];

  const filteredVoices = voices.filter(voice => {
    const matchesSearch = 
      voice.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voice.language.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGender = activeFilters.gender.length === 0 || 
      activeFilters.gender.includes(voice.gender);
    
    const matchesType = activeFilters.type.length === 0 || 
      activeFilters.type.includes(voice.quality);

    const matchesLanguage = activeFilters.language.length === 0 || 
      activeFilters.language.includes(voice.language);

    const matchesStyle = activeFilters.style.length === 0 ||
      (voice.styles && voice.styles.some(s => activeFilters.style.includes(s.toLowerCase()))) ||
      (voice.style && activeFilters.style.includes(voice.style.toLowerCase()));

    return matchesSearch && matchesGender && matchesType && matchesLanguage && matchesStyle;
  });

  const displayedVoices = filteredVoices.slice(0, showCount);

  const toggleFilter = (category: keyof typeof activeFilters, value: string) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <section id="voice-models" className="py-20 bg-gray-50 dark:bg-dark-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-gradient bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-[length:200%_auto] bg-clip-text text-transparent">
            500+ AI Voice Models - Best Text to Speech Voices 2025
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Choose from 500+ natural-sounding AI voices across 50+ languages. The best free AI voice generator library online.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
              <div className="flex items-center mb-4">
                <Filter className="w-5 h-5 mr-2 text-primary-600" />
                <h2 className="text-lg font-semibold">Filters</h2>
              </div>

              <div className="space-y-6">
                {/* Language Filter */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language
                  </h3>
                  <LanguageSelect
                    selectedLanguages={activeFilters.language}
                    onChange={(languages) => setActiveFilters(prev => ({
                      ...prev,
                      language: languages
                    }))}
                  />
                </div>

                {/* Voice Type Filter */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Voice Type
                  </h3>
                  <div className="space-y-2">
                    {voiceTypes.map(type => (
                      <label key={type.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={activeFilters.type.includes(type.id)}
                          onChange={() => toggleFilter('type', type.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                          {type.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Gender Filter */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gender
                  </h3>
                  <div className="space-y-2">
                    {['male', 'female'].map(gender => (
                      <label key={gender} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={activeFilters.gender.includes(gender)}
                          onChange={() => toggleFilter('gender', gender)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {gender}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Style Filter */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Style
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                    {styles.map(style => (
                      <label key={style.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={activeFilters.style.includes(style.id)}
                          onChange={() => toggleFilter('style', style.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                          {style.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Voice Grid */}
          <div className="flex-1">
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search voices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedVoices.map((voice) => (
                <VoiceCard 
                  key={voice.id} 
                  voice={voice} 
                  showFavorite={true}
                />
              ))}
            </div>

            {showCount < filteredVoices.length && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setShowCount(prev => prev + 9)}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Volume2 className="w-5 h-5 mr-2" />
                  Load More Voices
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VoiceModels;